import type { ComponentChildren } from 'preact';
import { useLayoutEffect, useRef } from 'preact/hooks';
import { closeSheet } from './ui.js';

interface SheetProps {
  title: string;
  children: ComponentChildren;
  /** Called after the sheet closes (a calibration run is cancelled, for one). */
  onClose?: () => void;
}

const FOCUSABLE = 'button:not([disabled]), input:not([disabled]), select:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

/**
 * A bottom sheet: a modal dialog with a title and a Close button. Focus moves into it and comes back to the control that opened it;
 * Tab stays inside; Escape, the Close button and a tap outside close it. It does not animate (DESIGN §4: nothing competes with the pulse).
 */
export function Sheet({ title, children, onClose }: SheetProps) {
  const box = useRef<HTMLDivElement>(null);

  // Focus moves in before the first paint, and Escape is heard wherever the focus is.
  useLayoutEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    box.current?.focus();
    return () => {
      onClose?.();
      if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
    };
  }, []);

  useLayoutEffect(() => {
    const onEscape = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      closeSheet();
    };
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, []);

  function onKeyDown(e: KeyboardEvent) {
    if (e.key !== 'Tab' || !box.current) return;
    const items = [...box.current.querySelectorAll<HTMLElement>(FOCUSABLE)].filter((el) => el.offsetParent !== null);
    if (items.length === 0) return;
    const first = items[0]!;
    const last = items[items.length - 1]!;
    const active = document.activeElement;
    if (e.shiftKey && (active === first || active === box.current)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
  }

  return (
    <div class="sheet-root" onPointerDown={(e) => { if (e.target === e.currentTarget) closeSheet(); }}>
      <div class="sheet" role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} ref={box} onKeyDown={onKeyDown}>
        <div class="sheet-head">
          <h2>{title}</h2>
          <button type="button" class="btn quiet" onClick={() => closeSheet()}>Close</button>
        </div>
        <div class="sheet-body">{children}</div>
      </div>
    </div>
  );
}
